import { Box } from '@mui/material';
import {
  FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import { AutocompleteOptions } from '@Components/Autocomplete';
import { DateState } from '@Components/DateRangePicker';
import Modal from '@Components/Modal';
import ReportConfig, {
  FilterStateType,
  ReportData,
} from '@Components/ReportConfig';
import { CommonConstants } from '@Constants/index';
import { useGetSessionsQuery } from '@Services/report.service';
import { useGetUsersListQuery } from '@Services/users.service';
import {
  BaseAlertProps,
  ReportTypes,
  VoiceProviderTypes,
} from '@Typings/global.types';
import { getDateFromISODateTimeString } from '@Utils/common/common.utils';
import query from '@Utils/common/query.utils';
import useAlert from '@Utils/hooks/useAlert.hook';
import useRoute from '@Utils/hooks/useRoute.hook';
import {
  getAccounts,
  getAccountsRTKQuery,
  getSessions,
  getSessionsRTKQuery,
} from '@Utils/report/report.utils';

import ReportFiltersModalProps from './ReportFiltersModal.types';

const ReportFiltersModal: FunctionComponent<ReportFiltersModalProps> = (
  props: ReportFiltersModalProps
): JSX.Element => {
  /** PROPS */
  const { open, setOpen, isAdmin, ...resProps } = props;

  /** HOOKS */
  const { t } = useTranslation();
  const location = useLocation();
  const { changeRoute } = useRoute();
  const { triggerAlert } = useAlert();

  /** STATES */
  const [dateState, setDateState] = useState<[DateState]>([
    {
      startDate: undefined,
      endDate: undefined,
      key: 'selection',
    },
  ]);
  const [filterState, setFilterState] = useState<FilterStateType>({
    status: [],
    disposition: [],
    callStatus: [],
  });
  const [voiceProviderValue, setVoiceProviderValue] =
    useState<VoiceProviderTypes>(VoiceProviderTypes.Both);
  const [sessionAutocompleteValue, setSessionAutocompleteValue] = useState<
    AutocompleteOptions[]
  >([]);
  const [accountAutocompleteValue, setAccountAutocompleteValue] = useState<
    AutocompleteOptions[]
  >([]);
  const [accountCount, setAccountCount] = useState<number>(0);
  const [alertState, setAlertState] = useState<BaseAlertProps>({
    openAlert: false,
  });

  /** RTK QUERY HOOKS --> */
  /** GET ACCOUNTS */
  const {
    data: accountsData,
    isLoading: accountsIsLoading,
    isFetching: accountsIsFetching,
    error: accountsError,
  } = useGetUsersListQuery(
    useMemo(() => getAccountsRTKQuery(accountCount), [accountCount]),
    {
      skip: !isAdmin,
    }
  );

  /** GET SESSIONS */
  const {
    data: sessionsData,
    isFetching: sessionsIsFetching,
    isLoading: sessionsIsLoading,
    error: sessionsError,
  } = useGetSessionsQuery(getSessionsRTKQuery(accountAutocompleteValue), {
    skip: isAdmin && !accountAutocompleteValue.length,
  });
  /** <-- RTK QUERY HOOKS */

  /** Fetches All the Accounts */
  useEffect(() => {
    if (accountCount !== accountsData?.data?.length) {
      setAccountCount(accountsData?.total || 0);
    }
  }, [accountsData, accountCount]);

  /** COMBINED VARIABLES --> */
  const error = accountsError || sessionsError;
  /** <-- COMBINED VARIABLES */

  /** Show error for get query */
  useEffect(() => {
    if (error) {
      triggerAlert(error, setAlertState);
    }
  }, [error, triggerAlert]);

  /** Filter Options (Accounts & Sessions) */
  const accountsOptions = useMemo(
    () => getAccounts(accountsData?.data),
    [accountsData?.data]
  );
  const sessionsOptions = useMemo(() => {
    if (isAdmin) {
      return accountAutocompleteValue.length ? getSessions(sessionsData) : [];
    }

    return getSessions(sessionsData);
  }, [accountAutocompleteValue.length, sessionsData, isAdmin]);

  /** Syncs the state according to URL Query Params */
  const syncState = useCallback(() => {
    /** URL PARAMS */
    const listTypeParam =
      query.getAll(CommonConstants.QUERY_PARAMS.COMMON.LIST_TYPE) || [];
    const callStatusParam =
      query.getAll(CommonConstants.QUERY_PARAMS.REPORT.CALL_STATUS) || [];
    const userIdsParam =
      query.getAll(CommonConstants.QUERY_PARAMS.REPORT.USER_IDS) || [];
    const dispositionParam =
      query.getAll(CommonConstants.QUERY_PARAMS.REPORT.DISPOSITION) || [];
    const dateStartParam = getDateFromISODateTimeString(
      query.get(CommonConstants.QUERY_PARAMS.COMMON.DATE_START) as string
    );
    const dateEndParam = getDateFromISODateTimeString(
      (query.get(CommonConstants.QUERY_PARAMS.COMMON.DATE_END) as string) || ''
    );
    const voiceProviderParam =
      (query.get(
        CommonConstants.QUERY_PARAMS.REPORT.VOICE_PROVIDER
      ) as VoiceProviderTypes) || VoiceProviderTypes.Both;

    /** SET STATES */
    const newAccountAutocompleteValue = accountsOptions?.filter((option) =>
      userIdsParam.includes(option.value)
    );
    setAccountAutocompleteValue(newAccountAutocompleteValue);
    setDateState((prevState) => [
      {
        ...prevState[0],
        startDate: dateStartParam,
        endDate: dateEndParam,
      },
    ]);
    setFilterState({
      status: listTypeParam,
      disposition: dispositionParam,
      callStatus: callStatusParam,
    });
    setVoiceProviderValue(voiceProviderParam);
  }, [accountsOptions]);

  /** Syncs the Sessions Autocomplete State with URL Params */
  const syncSessionsState = useCallback(() => {
    /** URL PARAMS */
    const sessionIdParam =
      query.getAll(CommonConstants.QUERY_PARAMS.REPORT.SESSION_ID) || [];

    const newSessionAutocompleteValue = sessionsOptions?.filter((option) =>
      sessionIdParam.includes(option.value)
    );

    /** SET STATES */
    setSessionAutocompleteValue(newSessionAutocompleteValue);
  }, [sessionsOptions]);

  useEffect(() => {
    syncState();
  }, [location.search, syncState]);

  useEffect(() => {
    syncSessionsState();
  }, [location.search, syncSessionsState]);

  /** MODAL HEADING */
  const headerText = t('reports.Report Filtering');

  /** CLOSE MODAL HANDLER */
  const handleClose = () => {
    syncState();
    syncSessionsState();
    setOpen(false);
  };

  /** APPLY FILTER CLICK HANDLER */
  const applyFilterHandler = (reportData: ReportData) => {
    setOpen(false);

    const {
      sessions,
      accounts = [],
      filters: { callStatus, status, disposition },
      startDate,
      endDate,
      voiceProvider,
      type,
    } = reportData;

    const searchParams = query.setAll({
      [CommonConstants.QUERY_PARAMS.COMMON.PAGE_SIZE]:
        CommonConstants.INITIAL_ROWS_PER_PAGE,
      [CommonConstants.QUERY_PARAMS.COMMON.PAGE_NO]:
        CommonConstants.INITIAL_PAGE_NO,
      [CommonConstants.QUERY_PARAMS.REPORT.USER_IDS]: accounts,
      [CommonConstants.QUERY_PARAMS.REPORT.SESSION_ID]: sessions,
      [CommonConstants.QUERY_PARAMS.REPORT.CALL_STATUS]: callStatus,
      [CommonConstants.QUERY_PARAMS.COMMON.LIST_TYPE]: status,
      [CommonConstants.QUERY_PARAMS.REPORT.DISPOSITION]: disposition,
      [CommonConstants.QUERY_PARAMS.COMMON.DATE_START]:
        startDate?.toISOString() || '',
      [CommonConstants.QUERY_PARAMS.COMMON.DATE_END]:
        endDate?.toISOString() || '',
      ...(type !== ReportTypes.EmailLog && {
        [CommonConstants.QUERY_PARAMS.REPORT.VOICE_PROVIDER]:
          voiceProvider || VoiceProviderTypes.Both,
      }),
    });

    changeRoute(searchParams);
  };

  return (
    <Modal
      alertState={alertState}
      sx={{
        '& .content': {
          maxWidth: '66vw',
        },
      }}
      open={open}
      headerText={headerText}
      onClose={handleClose}
    >
      <Box p={2.4} maxHeight="71vh" overflow="auto">
        <ReportConfig
          filterState={filterState}
          setFilterState={setFilterState}
          dateRangeProps={{
            dateState,
            setDateState,
          }}
          accountAutocompleteProps={
            isAdmin
              ? {
                  options:
                    accountsOptions.length === accountCount
                      ? accountsOptions
                      : [],
                  limitTags: CommonConstants.LIMIT_TAGS,
                  accountAutocompleteValue,
                  setAccountAutocompleteValue,
                  loading:
                    accountsIsLoading || accountsIsFetching || !accountCount,
                }
              : undefined
          }
          sessionAutocompleteProps={{
            options: sessionsOptions,
            limitTags: CommonConstants.LIMIT_TAGS,
            sessionAutocompleteValue,
            setSessionAutocompleteValue,
            loading: sessionsIsLoading || sessionsIsFetching,
          }}
          type="modal"
          primaryBtnProps={{
            text: t('common.messages.Apply'),
            onClick: applyFilterHandler,
          }}
          secondaryBtnProps={{
            text: t('common.messages.Cancel'),
            onClick: handleClose,
          }}
          {...resProps}
          setVoiceProviderProps={setVoiceProviderValue}
          voiceProviderProps={voiceProviderValue}
        />
      </Box>
    </Modal>
  );
};

export default ReportFiltersModal;
